import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid
} from 'recharts';
import { fetchClaimForecast } from '../../services/analyticsApi';
import { fetchVehicleCatalogue } from '../../services/vehicleCatalogue';
import {
  ALL_MANUFACTURERS, ALL_MODELS, CURRENT_DATE, OTHER_GROUP, PREDICTED_OPACITY, colorsFor, groupDailyData, processTimeSeriesData,
} from './modelClaimsData';
import { PartWiseClaimsModal, TimeSeriesTooltip } from './PartWiseClaimsModal';
import { colors } from '../../theme';

// Re-exported for existing importers.
export { ALL_MANUFACTURERS, groupDailyData };

const ModelClaimsDashboard = () => {
  const [dailyClaimsData, setDailyClaimsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [catalogue, setCatalogue] = useState(null);       // null while loading or when unavailable
  const [catalogueFailed, setCatalogueFailed] = useState(false);

  const [selectedPeriod, setSelectedPeriod] = useState('YTD');
  const [selectedManufacturer, setSelectedManufacturer] = useState(ALL_MANUFACTURERS);
  const [selectedModel, setSelectedModel] = useState(ALL_MODELS);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalHeader, setModalHeader] = useState('');
  const [modalModel, setModalModel] = useState('');
  const [modalActual, setModalActual] = useState({});
  const [modalPredicted, setModalPredicted] = useState({});

  const loadForecast = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchClaimForecast()
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('API error'))))
      .then((json) => {
        const dataObj = json?.data || json?.Data || json?.result || {};
        setDailyClaimsData(dataObj);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadForecast();
  }, [loadForecast]);

  useEffect(() => {
    let active = true;
    fetchVehicleCatalogue()
      .then((data) => active && setCatalogue(data))
      .catch(() => active && setCatalogueFailed(true));
    return () => { active = false; };
  }, []);

  // dashboard_key (the forecast's model key) -> catalogue model
  const modelByKey = useMemo(() => {
    const map = {};
    (catalogue?.models || []).forEach((m) => { map[m.dashboard_key] = m; });
    return map;
  }, [catalogue]);

  const allModelNames = useMemo(() => {
    if (!dailyClaimsData) return [];
    const s = new Set();
    Object.values(dailyClaimsData).forEach((dateEntry) => {
      Object.keys(dateEntry || {}).forEach((m) => s.add(m));
    });
    return Array.from(s).sort();
  }, [dailyClaimsData]);

  const manufacturers = useMemo(() => {
    const list = (catalogue?.manufacturers || []).map((m) => ({ code: m.manufacturer_code, name: m.manufacturer_name }));
    if (catalogue && allModelNames.some((k) => !modelByKey[k])) list.push({ code: OTHER_GROUP, name: 'Other' });
    return list;
  }, [catalogue, allModelNames, modelByKey]);

  const manufacturerOf = useCallback((key) => modelByKey[key]?.manufacturer_code || OTHER_GROUP, [modelByKey]);
  const modelLabel = useCallback((key) => modelByKey[key]?.model_name || key.replace(/_/g, ' '), [modelByKey]);

  // With a single manufacturer there is nothing to choose: its models are shown directly and the
  // manufacturer selector is hidden. With several, the selector works as before.
  const singleManufacturer = manufacturers.length === 1 ? manufacturers[0].code : null;
  const manufacturerInScope = singleManufacturer || selectedManufacturer;

  // Without a catalogue the chart stays model-level (the previous behaviour).
  const byManufacturer = Boolean(catalogue) && manufacturerInScope === ALL_MANUFACTURERS;

  // Ordered by display name (also the fixed colour order within the scope).
  const modelsInScope = useMemo(() => (
    !catalogue || manufacturerInScope === ALL_MANUFACTURERS
      ? allModelNames
      : allModelNames.filter((k) => manufacturerOf(k) === manufacturerInScope)
  ).slice().sort((a, b) => modelLabel(a).localeCompare(modelLabel(b))),
  [catalogue, manufacturerInScope, allModelNames, manufacturerOf, modelLabel]);

  useEffect(() => {
    if (selectedModel !== ALL_MODELS && !modelsInScope.includes(selectedModel)) {
      setSelectedModel(ALL_MODELS);
    }
  }, [modelsInScope, selectedModel]);

  // Series shown: one per manufacturer (summed), or the selected manufacturer's models, or one model.
  const seriesData = useMemo(
    () => (byManufacturer ? groupDailyData(dailyClaimsData, manufacturerOf) : dailyClaimsData),
    [byManufacturer, dailyClaimsData, manufacturerOf]
  );
  const displayedSeries = useMemo(() => {
    if (byManufacturer) return manufacturers.map((m) => m.code).filter((c) => allModelNames.some((k) => manufacturerOf(k) === c));
    return selectedModel === ALL_MODELS ? modelsInScope : [selectedModel];
  }, [byManufacturer, manufacturers, allModelNames, manufacturerOf, selectedModel, modelsInScope]);

  const seriesLabel = useCallback((key) => (
    byManufacturer ? (manufacturers.find((m) => m.code === key)?.name || key) : modelLabel(key)
  ), [byManufacturer, manufacturers, modelLabel]);

  // Fixed colour order: manufacturers in catalogue order; models in order within their scope.
  const seriesColors = useMemo(
    () => (byManufacturer ? colorsFor(manufacturers.map((m) => m.code)) : colorsFor(modelsInScope)),
    [byManufacturer, manufacturers, modelsInScope]
  );

  const { chartData, bucketPartsData } = useMemo(
    () => processTimeSeriesData(seriesData, selectedPeriod, displayedSeries),
    [seriesData, selectedPeriod, displayedSeries]
  );

  const scopeLabel = byManufacturer ? 'manufacturer wise' : 'model wise';
  const periodLabels = {
    YTD: `Yearly ${scopeLabel} Claims Distribution (${CURRENT_DATE.getFullYear()})`,
    MTD: `Monthly ${scopeLabel} Claims Distribution (${CURRENT_DATE.toLocaleDateString('en-US', { month: 'long' })})`,
    WTD: `Weekly ${scopeLabel} Claims Distribution`,
  };
  const chartTitle = periodLabels[selectedPeriod];

  const handleBarClick = (entry, seriesKey, series) => {
    if (!entry || !entry.payload) return;

    const { bucketKey, name, isFuture } = entry.payload;
    const bucket = bucketPartsData?.[bucketKey] || {};
    const partsOfModel = bucket?.[seriesKey] || { actual_parts: {}, predicted_parts: {} };

    const actualPartsForModal = isFuture ? {} : (partsOfModel.actual_parts || {});
    const predictedPartsForModal = partsOfModel.predicted_parts || {};

    setModalHeader(`${selectedPeriod} • ${name} • ${series === 'actual' ? 'Actual' : 'Predicted'}`);
    setModalModel(seriesLabel(seriesKey));
    setModalActual(actualPartsForModal);
    setModalPredicted(predictedPartsForModal);
    setModalOpen(true);
  };

  if (loading) return <div className="p-8 text-center text-xs uppercase tracking-widest">Loading...</div>;
  if (error) return (
    <div role="alert" className="p-8 text-center text-danger text-xs uppercase tracking-widest">
      Claims distribution could not be loaded ({error}). No figures are shown.
      <div>
        <button type="button" onClick={loadForecast} className="mt-4 bg-primary hover:bg-primary-dark text-white text-xs font-semibold py-2 px-6 rounded-none uppercase tracking-[0.15em]">
          Retry
        </button>
      </div>
    </div>
  );
  if (!dailyClaimsData) return <div className="p-8 text-center text-xs uppercase tracking-widest">No data available.</div>;

  const selectClass = "bg-white border border-line rounded-none py-2 px-4 text-xs uppercase tracking-widest focus:ring-primary focus:border-primary";

  return (
    <div className="bg-white p-6 rounded-none border border-line font-sans">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <h1 className="text-sm font-light text-primary uppercase tracking-widest">{chartTitle}</h1>

        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className={selectClass}
            aria-label="Select period"
          >
            <option value="YTD">YTD</option>
            <option value="MTD">MTD</option>
            <option value="WTD">WTD</option>
          </select>

          {catalogue && !singleManufacturer && (
            <select
              value={selectedManufacturer}
              onChange={(e) => { setSelectedManufacturer(e.target.value); setSelectedModel(ALL_MODELS); }}
              className={selectClass}
              aria-label="Select manufacturer"
            >
              <option value={ALL_MANUFACTURERS}>All Manufacturers</option>
              {manufacturers.map((m) => (
                <option key={m.code} value={m.code}>{m.name}</option>
              ))}
            </select>
          )}

          {!byManufacturer && (
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className={selectClass}
              aria-label="Select model"
            >
              <option value={ALL_MODELS}>All Models</option>
              {modelsInScope.map((m) => (
                <option key={m} value={m}>{modelLabel(m)}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {catalogueFailed && (
        <p role="status" className="mb-4 text-[10px] text-gray-500 uppercase tracking-widest">
          Vehicle catalogue unavailable - showing claims by model only.
        </p>
      )}

      <div className="w-full h-[560px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={colors.line} />
            <XAxis dataKey="name" tick={{ fill: colors.primary, fontSize: 10, fontFamily: "'Inter', sans-serif" }} axisLine={{ stroke: colors.line }} tickLine={false} />
            <YAxis tick={{ fill: colors.primary, fontSize: 10, fontFamily: "'Inter', sans-serif" }} axisLine={false} tickLine={false} />
            <Tooltip content={<TimeSeriesTooltip />} wrapperStyle={{ opacity: 1, zIndex:100 }} cursor={{ fill: colors.background }} />
            <Legend wrapperStyle={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: '600' }} iconType="rect" />
            {displayedSeries.map((key) => (
              <React.Fragment key={key}>
                <Bar
                  dataKey={`${key}_actual`}
                  name={`${seriesLabel(key)} Actual`}
                  fill={seriesColors[key]}
                  stackId="actual"
                  onClick={(entry) => handleBarClick(entry, key, 'actual')}
                  className="cursor-pointer"
                />
                <Bar
                  dataKey={`${key}_predicted`}
                  name={`${seriesLabel(key)} Predicted`}
                  fill={seriesColors[key]}
                  fillOpacity={PREDICTED_OPACITY}
                  stackId="predicted"
                  onClick={(entry) => handleBarClick(entry, key, 'predicted')}
                  className="cursor-pointer"
                />
              </React.Fragment>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <PartWiseClaimsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        header={modalHeader}
        modelName={modalModel}
        actualParts={modalActual}
        predictedParts={modalPredicted}
      />
    </div>
  );
};

export default ModelClaimsDashboard;
